import { createSelector } from "reselect";
import memoize from "lodash.memoize";
import Immutable from "immutable";

import stringify from "json-stable-stringify";
import RequestState from "services/requestState";
import { ResourceModelTypes, ResourceType } from "resourceModels";
import { ResourcesState } from "../reducers/resources/types";

interface State {
  resources: ResourcesState
}

function getResourceState(
  state: State, resourceType: ResourceType
): Immutable.Map<string, unknown> {
  const resourceState = state.resources[resourceType];

  if (!resourceState) {
    throw Error(`Resource \`${resourceType}\` is not registered in state`);
  }

  return resourceState;
}

export function createGetResourceRecordsSelector(
  resourceType: ResourceType
): (state: State) => Record<string, unknown> {
  return state => getResourceState(state, resourceType).getIn(["byId"]);
}

export function createGetAllResourcesRequestSelector(
  resourceType: ResourceType
): (state: State) => Record<string, unknown> {
  return state => getResourceState(state, resourceType).getIn(["requests", "all"]);
}

export function createGetPatchOneMessageTemplatesRequestSelector(
  resourceType: ResourceType
): (state: State) => Record<string, unknown> {
  return state => getResourceState(state, resourceType).getIn(["requests", "patchOne"]);
}

export function createGetAllMessageTemplatesSelector(
  resourceType: ResourceType,
  getResourceRecordsSelector: () => Immutable.Map<string, unknown>,
  getAllResourcesRequestSelector: () => RequestState
): (state: State) => Record<string, unknown> {
  return createSelector(
    getResourceRecordsSelector,
    getAllResourcesRequestSelector,
    (records, request) => {
      if (request && request.get("result")) {
        return request.get("result").map((id: string) => records.get(id));
      }

      return null;
    }
  );
}

export const getResourcesSelector = memoize(resourceType => createSelector(
  (state: State) => getResourceState(state, resourceType),

  resourceState => memoize((requestParams: unknown) => {
    const request = resourceState.getIn([
      "requests",
      requestParams ? stringify(requestParams) : "all"
    ]);

    if (!request || !request.get("result")) {
      return null;
    }

    return request.get("result").map((id: string) => resourceState.getIn(["byId", id]));
  }, requestParams => stringify(requestParams))
));

export const getResourcesRequestSelector = memoize(resourceType => createSelector(
  (state: State) => state.resources[resourceType],

  resourceState => memoize((requestParams: string | unknown) => {
    let requestKey;

    if (typeof requestParams === "string") {
      requestKey = requestParams;
    } else if (requestParams) {
      requestKey = stringify(requestParams);
    } else {
      requestKey = "all";
    }

    const request = resourceState.getIn(["requests", requestKey]);

    if (!request) {
      return null;
    }

    return request;
  })
));

export function getResources<K extends ResourceType>(
  state: State, resourceType: K, requestParams?: unknown
): Immutable.List<ResourceModelTypes[K]> | null {
  return getResourcesSelector(resourceType)(state)(requestParams);
}

export function getResourcesRequest(
  state: State, resourceType: ResourceType, requestParams: unknown | string
): RequestState | null {
  return getResourcesRequestSelector(resourceType)(state)(requestParams);
}

export function getResource<K extends ResourceType>(
  state: State, resourceType: K, id: string | null = null
): ResourceModelTypes[K] | null {
  const resourceId = (
    id ||
    getResourceState(state, resourceType).getIn(["requests", "default", "result"])
  );

  return state.resources[resourceType].getIn(["byId", resourceId]) || null;
}
